import {
  Dialog,
  Box,
  DialogTitle,
  DialogContent,
  ThemeProvider,
  Button,
  DialogActions,
  InputLabel,
  Select,
  MenuItem,
  FormControl,
} from '@mui/material';
import { ClearIcon } from '@mui/x-date-pickers-pro';
import {
  customButtonTheme,
  customFileButtonTheme,
  customRedButtonTheme,
} from 'assets/styles/themes/generic_button_mui';
import { useContext, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { displayErrorMessage, displayMessage, formatFileSize } from 'utils/tools_functions';
import { CloudUpload } from '@mui/icons-material';
import { ExploitationContext, LoadingContext } from 'utils/context';
import { customDialogImportTheme } from 'assets/styles/themes/Dialog/generic_dialog_import';
import { AxiosError } from 'axios';
import { useCheckStatus } from 'providers/ImportStatus';
import { IMPORT_FROM } from 'utils/GlobalVariables';
import { useTranslation } from 'react-i18next';

const ImportModalWithGroundType = ({
  open,
  setModalOpen,
  nextCampaignChosen,
  from,
  config,
}) => {
  const { t } = useTranslation();
  const [file, setFile] = useState(null);
  const { setCustomText } = useContext(LoadingContext);
  const { groundTypesList, getGroundTypesList } = useContext(ExploitationContext);
  const [currentGroundType, setCurrentGroundType] = useState('');
  const exploitationId = localStorage.getItem('exploitation');
  const checkStatus = useCheckStatus();

  const chooseFile = async (e) => {
    setCurrentGroundType('');
    if (!e.target.files) return;
    if (e?.target?.files[0]) {
      setFile(e?.target?.files[0]);
    }
  };

  useEffect(() => {
    if (groundTypesList.length === 0) {
      getGroundTypesList();
    }
  }, []);

  const handleChangeGroundType = (event) => {
    setCurrentGroundType(event.target.value);
  };

  const importDataFromFile = async () => {
    setModalOpen(false);
    setCustomText(displayMessage(config.loadingKey));
    const formData = new FormData();
    formData.append('file', file);
    formData.append('groundTypeId', currentGroundType);
    if (nextCampaignChosen) {
      formData.append('expectedForecastCampaign', nextCampaignChosen.stringFormat);
    }
    if (from === IMPORT_FROM.procedure && nextCampaignChosen) {
      formData.append('expectedForecastCampaign', nextCampaignChosen?.stringFormat);
      if (exploitationId !== null) {
        formData.append('procedureExploitation', exploitationId);
      } else {
        toast.error(displayErrorMessage('ERR_IMPORT_MISSING_EXPLOITATION_ID'));
      }
    }
    try {
      const response = await config.apiCall(formData, exploitationId);
      checkStatus(response.data.data.jobId);
    } catch (error) {
      if (error instanceof AxiosError) {
        toast.error(error?.response?.data?.message ?? displayErrorMessage(config.errorKey));
      } else {
        toast.error(displayErrorMessage('ERR_UNEXPECTED'));
      }
    }
  };

  return (
    <ThemeProvider theme={customDialogImportTheme}>
      <Dialog maxWidth="lg" fullWidth open={open}>
        <Box className="modal_header_wrapper">
          <DialogTitle className="modal_header_title">
            {t(config.titleKey)}
          </DialogTitle>
          <ClearIcon
            className="modal_header_close_icon"
            onClick={() => {
              setModalOpen(false);
              setFile(null);
            }}
          />
        </Box>
        <DialogContent>
          <Box className="modal_main_content">
            <ThemeProvider theme={customFileButtonTheme}>
              <Button component="label" startIcon={<CloudUpload />}>
                {file ? t('button.chooseAnotherFile') : t('button.chooseFile')}
                <input
                  type="file"
                  onChange={(e) => chooseFile(e)}
                  accept={config.accept}
                  id="file"
                  hidden
                />
              </Button>
            </ThemeProvider>
            {file && (
              <Box className="file_info_wrapper">
                <div className="file_info">
                  <label htmlFor="file_name">{t('label.fileName')} :</label>
                  <span id="file_name"> {file?.name}</span>
                </div>
                <div className="file_info">
                  <label htmlFor="file_size">{t('label.fileSize')} :</label>
                  <span id="file_size"> {formatFileSize(file?.size)}</span>
                </div>
              </Box>
            )}
          </Box>
          {groundTypesList && file && (
            <FormControl size="small" sx={{ minWidth: 200, maxWidth: 250, marginTop: '10px' }}>
              <InputLabel id="groundTypeList">{t('label.chooseGroundType')}</InputLabel>
              <Select
                autoWidth
                labelId="groundTypeList"
                id="groundTypeListSelect"
                value={currentGroundType}
                label={t('label.defaultGroundType')}
                onChange={handleChangeGroundType}
              >
                {groundTypesList.map((groundType) => (
                  <MenuItem key={groundType.id} value={groundType.id}>
                    {groundType.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </DialogContent>
        <DialogActions>
          <ThemeProvider theme={customButtonTheme}>
            <Button
              id="inputLaunchImport"
              disabled={file === null || currentGroundType === ''}
              onClick={importDataFromFile}
            >
              {t('button.launchImport')}
            </Button>
          </ThemeProvider>
          <ThemeProvider theme={customRedButtonTheme}>
            <Button
              id="inputCancelImport"
              onClick={() => {
                setModalOpen(false);
                setFile(null);
              }}
            >
              {t('common.cancel')}
            </Button>
          </ThemeProvider>
        </DialogActions>
      </Dialog>
    </ThemeProvider>
  );
};

export default ImportModalWithGroundType;
